import { Component, Event, EventEmitter, h, Host, Prop } from '@stencil/core';

/**
 * An element in the breadcrumb path. A list of these
 * is returned upon navigation events.
 */
export interface UUIBreadcrumbItem {
  /** The rendered label of the item */
  label: string;
  /** The path string of the item. */
  path: string;
  /** Hidden items are returned in the path but are not rendered. */
  hidden?: boolean;
  /** The url of the item. Used for rendering the default anchor link href. */
  url?: string;
  /** User data returned in navigation events. */
  data?: any;
}

/**
 * When the user selects a breadcrumb item, this is returned in the
 * `navigate` event.
 */
export type UUIBreadcrumbEvent = {
  /**
   * The items representing the clicked path in the breadcrumb, ordered
   * from root to tail.
   */
  items: UUIBreadcrumbItem[];
  /** The string path representation of the breadcrumb */
  path: string;
  /** The string path items only, as a convenient array. */
  pathItems: string[];
};

/**
 * UUIBreadcrumb is a standard breadcrumb navigation component.
 * It manages a path through a tree in a navigation hierarchy.
 *
 * @slot default - Slot for the content of the breadcrumb, typically
 * just the label text.
 */
@Component({
  tag: 'uui-breadcrumb',
  styleUrl: 'uui-breadcrumb.css',
  shadow: true
})
export class UUIBreadcrumb {
  /** disabled state for the breadcrumb */
  @Prop() disabled = false;
  /** The breadcrumb component can parse and render a string path, typically the pathName component of `window.location` */
  @Prop({ reflect: true }) path: string;
  /** Internal representation of the items in the breadcrumb path, but can be set directly for a richer routing api. */
  @Prop({ mutable: true }) pathItems: UUIBreadcrumbItem[];
  /** Navigate events fire whenever an item in the breadcrumb is selected. */
  @Event() navigate: EventEmitter<any>;

  componentWillLoad() {
    this.updateFromPath(this.path);
  }

  updateFromPath(newPath: string) {
    if (!newPath) {
      return;
    }
    // Break the path into items:
    const pathComponents = newPath.split('/');
    this.pathItems = [];
    const accumulatingPathElements: string[] = [];
    for (let i = 0; i < pathComponents.length; i++) {
      const path = pathComponents[i];
      if (!path) {
        // Handle leading or doubled "/" separators.
        continue;
      }
      accumulatingPathElements.push(path);
      const breadcrumbItem: UUIBreadcrumbItem = {
        label: path,
        path,
        url: accumulatingPathElements.join('/')
      };
      this.pathItems.push(breadcrumbItem);
    }
  }

  emitNavigation(e: Event, pathIndex: number) {
    e.preventDefault();
    e.stopPropagation();
    const items = this.pathItems.slice(0, pathIndex + 1);
    const pathItems = items.map(item => item.path);
    const path = pathItems.join('/');
    this.navigate.emit({ items, path, pathItems });
  }

  render() {
    const elementCount = this.pathItems?.length ?? 0;
    let lastIndex = elementCount - 1;

    // The last index is the index of the last *visible* item.
    for (let i = lastIndex; i >= 0; i--) {
      const item = this.pathItems[i];
      if (!item.hidden) {
        break;
      }
      lastIndex = i;
    }

    const pathElements = [];
    for (let i = 0; i < elementCount; i++) {
      const item = this.pathItems[i];
      if (item.hidden) {
        // Hidden items are not rendered
        continue;
      }
      if (i > 0) {
        pathElements.push(<span class="separator"></span>);
      }
      if (i === lastIndex) {
        pathElements.push(<span>{item.label}</span>);
      } else {
        pathElements.push(
          <a href={item.url ?? '#'} onClick={e => this.emitNavigation(e, i)}>
            {item.label}
          </a>
        );
      }
    }
    return <Host path={this.path}>{pathElements}</Host>;
  }
}
